import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import {ZINDEX_DND_CANVAS_DRAGGED_BLOCK} from 'styles/StyleConstants';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

import {DnDDragHandleProps, DRAG_HANDLE_CLASS} from './DnDDragHandle';
import {DND_BLOCK_HOVER_PREVIEW_WIDTH, DND_BLOCK_SPACING} from './DnDStyleConstants';
import {DragState, HoverPosition} from './hooks/useDnDCanvasDragState';

export function makeMinBlockWidthStyle(minBlockWidth: number): React.CSSProperties {
  // We use flexBasis to define the minimum width of the blocks. Although flex-basis isn't strictly a minimum
  // width property, we also set the flex-shrink property of blocks to 0 and the flex-grow to 1. So this means
  // "Initially, set the size of the block to minBlockWidth. Then, if there's room, grow it along with the
  // other blocks to fit, but never shrink.". I.e. the width of the object will always be >= minBlockWidth.
  //
  // We set the flexBasis in the style prop directly instead of Aphrodite because Aphrodite dndBlockStyles don't take
  // in parameters the way we want to with props.
  return {
    flexBasis: `${minBlockWidth}px`,
  };
}

export type DnDItemRenderFunction<T> = (
  item: T,
  dragHandleProps: DnDDragHandleProps,
  index: number
) => JSX.Element;

export interface DnDBlockProps<T> {
  dragState: DragState<T>;
  blockHeight?: number;
  index: number;
  item: T;
  minBlockWidth?: number;
  /** When true, DnDBlock sizes to its content instead of growing to fill available space */
  fitContent?: boolean;
  onDragStart: (e: ElementMouseEvent, item: T) => void;
  /**
   * Called on every hover position change during a drag (not just on initial entry).
   * The underlying event is an onMouseMove event to track left/right hover position.
   */
  onMouseEnterBlock: (hoveredItem: T, hoverPosition: HoverPosition) => void;
  onMouseLeaveBlock: () => void;
  renderItem: DnDItemRenderFunction<T>;
  disabled: boolean;
  disableHorizontalDrag?: boolean;
}

export interface DnDPlaceholderBlockProps {
  minBlockWidth?: number;
}

export function DnDPlaceholderBlock(props: DnDPlaceholderBlockProps): JSX.Element {
  return (
    <div
      className={css(dndBlockStyles.block)}
      style={props.minBlockWidth ? makeMinBlockWidthStyle(props.minBlockWidth) : undefined}
      {...getTestProps({component: 'DnDPlaceholderBlock', element: 'root'})}
    />
  );
}

function chooseStyle<T>(
  dragState: DragState<T>,
  disableHorizontalDrag?: boolean
): {blockStyle: object; hoveredPreviewStyle: object} {
  if (!disableHorizontalDrag) {
    const hoveredPreviewStyle =
      dragState.isDragging && dragState.isHovering && dragState.hoverPosition === 'left'
        ? dndBlockStyles.hoveredPreviewBlockHorizontalLeft
        : dndBlockStyles.hoveredPreviewBlockHorizontalRight;
    return {
      blockStyle: dndBlockStyles.blockHorizontalDnD,
      hoveredPreviewStyle,
    };
  }
  if (dragState.isDragging && dragState.dragDelta.translateY > 0) {
    return {
      blockStyle: dndBlockStyles.blockVerticalDnd,
      hoveredPreviewStyle: dndBlockStyles.hoveredPreviewBlockVerticalBottom,
    };
  }
  return {
    blockStyle: dndBlockStyles.blockVerticalDnd,
    hoveredPreviewStyle: dndBlockStyles.hoveredPreviewBlockVerticalTop,
  };
}

export default function DnDBlock<T>(props: DnDBlockProps<T>): JSX.Element {
  const {disabled, dragState, blockHeight, index, item, onDragStart, renderItem} = props;
  const blockRef = useRef<HTMLDivElement>(null);
  // Deduplicates continuous onMouseMove events — only dispatches when the hover side changes.
  const lastDispatchedPositionRef = useRef<HoverPosition | null>(null);

  const handleOnDragStart = useCallback(
    (e: ElementMouseEvent) => {
      onDragStart(e, item);
    },
    [onDragStart, item]
  );

  const calculateHoverPosition = useCallback((clientX: number): HoverPosition => {
    if (!blockRef.current) {
      return 'right';
    }
    const rect = blockRef.current.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    return clientX < midpoint ? 'left' : 'right';
  }, []);

  function handleOnMouseMove(e: React.MouseEvent<HTMLDivElement>): void {
    if (!dragState.isDragging) {
      return;
    }
    const hoverPosition = calculateHoverPosition(e.clientX);
    if (lastDispatchedPositionRef.current === hoverPosition) {
      return;
    }
    lastDispatchedPositionRef.current = hoverPosition;
    props.onMouseEnterBlock(item, hoverPosition);
  }

  function handleOnMouseLeave(): void {
    lastDispatchedPositionRef.current = null;
    props.onMouseLeaveBlock();
  }

  useEffect(() => {
    if (!dragState.isDragging) {
      lastDispatchedPositionRef.current = null;
    }
  }, [dragState.isDragging]);

  // The goal of the useMemo is to never re-render the renderedItem, which is why wrap it in divs bellow for
  // all DnD related interactions on blocks other than the drag handle, so when adding properties to drag handle
  // props, they should not be properties that change because of DnD interactions. Adding properties like
  // "isDragging" for example would trigger re-renders of renderedItem every time the user picks up or drops a
  // block, which is problematic for expensive-to-render layouts such as Insights (causes significant lag in
  // the Insights case).
  const renderedItem = useMemo(
    () =>
      renderItem(
        item,
        {
          className: DRAG_HANDLE_CLASS,
          onMouseDown: disabled ? noop : handleOnDragStart,
        },
        index
      ),
    [disabled, handleOnDragStart, index, item, renderItem]
  );

  const {blockStyle, hoveredPreviewStyle} = chooseStyle(dragState, props.disableHorizontalDrag);
  return (
    <div
      ref={blockRef}
      className={css(
        dndBlockStyles.block,
        props.fitContent && dndBlockStyles.blockFitContent,
        blockStyle,
        dragState.isDragging && item === dragState.draggedItem && dndBlockStyles.draggedBlock,
        dragState.isDragging &&
          dragState.isHovering &&
          item === dragState.hoveredItem &&
          !disabled &&
          hoveredPreviewStyle
      )}
      onMouseMove={disabled ? undefined : handleOnMouseMove}
      onMouseLeave={disabled ? undefined : handleOnMouseLeave}
      style={{
        ...(props.minBlockWidth && !props.fitContent ? makeMinBlockWidthStyle(props.minBlockWidth) : {}),
        ...(dragState.isDragging && item === dragState.draggedItem
          ? {
              transform: `translate3d(${
                props.disableHorizontalDrag ? 0 : dragState.dragDelta.translateX
              }px, ${dragState.dragDelta.translateY}px, 0)`,
            }
          : {}),
        height: blockHeight,
      }}
      {...getTestProps({component: 'DnDBlock', element: 'root'})}
    >
      <div
        className={classNames(
          {'u-no-mouse-target': dragState.isDragging},
          css(dndBlockStyles.mouseEventWrapper)
        )}
        {...getTestProps({component: 'DnDBlock', element: 'mouseEventWrapper'})}
      >
        {renderedItem}
      </div>
    </div>
  );
}

export const dndBlockStyles = StyleSheet.create({
  block: {
    flexShrink: 0,
    flexGrow: 1,
    // min-width needed to override "auto" behavior, which might make the min-width the size of the blocks'
    // content or in some browsers (Firefox) it becomes the full screen width in some cases
    minWidth: 0,
  },
  // Override flex behavior to size DnDBlock to its content instead of growing to fill space
  blockFitContent: {
    flexGrow: 0,
    width: 'fit-content',
  },
  draggedBlock: {
    pointerEvents: 'none',
    opacity: 0.9,
    // We need to set the z-index to a value higher than anything that is rendered in the blocks in order
    // to ensure that the contents of other blocks do not render over the dragged item.
    zIndex: ZINDEX_DND_CANVAS_DRAGGED_BLOCK,
  },
  mouseEventWrapper: {
    height: '100%',
  },
  hoveredPreviewBlockHorizontalRight: {
    borderRightColor: BLUE_LIGHT,
  },
  hoveredPreviewBlockHorizontalLeft: {
    borderLeftColor: BLUE_LIGHT,
  },
  hoveredPreviewBlockVerticalTop: {
    borderTopColor: BLUE_LIGHT,
  },
  hoveredPreviewBlockVerticalBottom: {
    borderBottomColor: BLUE_LIGHT,
  },
  blockHorizontalDnD: {
    marginBottom: DND_BLOCK_SPACING,
    // To achieve the block hover preview on either side of a block we use transparent borders that get
    // recolored on hover. The same technique is applied symmetrically to both left and right sides:
    // 1. Each side of a block gets a transparent border, padding, and margin that together equal half the
    //    column spacing. Two adjacent blocks therefore produce the full column spacing between them.
    // 2. The hover preview recolors the transparent border on the appropriate side. Because the border is
    //    always present, showing the preview doesn't shift the layout.
    // 3. The formula (SPACING/2 - BORDER_WIDTH) / 2 splits the remaining space (after the border) evenly
    //    between padding and margin so the border sits centered within each block's half of the gap.
    borderRight: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,
    paddingRight: (DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH) / 2,
    marginRight: (DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH) / 2,
    borderLeft: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,
    paddingLeft: (DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH) / 2,
    marginLeft: (DND_BLOCK_SPACING / 2 - DND_BLOCK_HOVER_PREVIEW_WIDTH) / 2,
  },
  blockVerticalDnd: {
    borderTop: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,
    borderBottom: `${DND_BLOCK_HOVER_PREVIEW_WIDTH}px solid transparent`,

    // Negative margin to cancel out border
    marginTop: -1 * DND_BLOCK_HOVER_PREVIEW_WIDTH,
    marginBottom: -1 * DND_BLOCK_HOVER_PREVIEW_WIDTH,
  },
});
